import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

interface ProjectPreview {
  id: number;
  title: string;
  client: string;
  category: string;
  year: string;
  tagline: string;
  heroImage: string;
  keyFindings: {
    title: string;
    description: string;
    icon: string;
  }[];
  quickStats: {
    value: string;
    label: string;
  }[];
  previewImages: string[];
  challenge: string;
  solution: string;
}

export default function ProjectPreview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showNav, setShowNav] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowNav(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const projects: Record<string, ProjectPreview> = {
    '1': {
      id: 1,
      title: 'Netflix Case Study',
      client: 'Netflix',
      category: 'Product Design',
      year: '2023',
      tagline: 'Reimagining content discovery and personalization for millions of streaming users',
      heroImage: 'https://cdn.prod.website-files.com/5f3bf8a18cfff95974f80423/658daac2f2e0bbe244e1f5c8_Screen%201.jpg',
      keyFindings: [
        {
          title: 'Content Overload',
          description: 'Users were overwhelmed by the vast content library, spending an average of 18 minutes browsing before making a selection',
          icon: 'ri-stack-line'
        },
        {
          title: 'Poor Personalization',
          description: 'Existing recommendation algorithms failed to surface relevant content, leading to 42% of users abandoning their search',
          icon: 'ri-user-search-line'
        },
        {
          title: 'Navigation Complexity',
          description: 'The interface structure created cognitive load, with users struggling to navigate between categories and genres',
          icon: 'ri-route-line'
        },
        {
          title: 'Engagement Drop',
          description: 'User engagement metrics showed declining watch time and increased churn rates among key demographics',
          icon: 'ri-line-chart-line'
        }
      ],
      quickStats: [
        { value: '45%', label: 'Increased Engagement' },
        { value: '32%', label: 'Better Discovery' },
        { value: '4.8', label: 'User Rating' },
        { value: '28%', label: 'Reduced Churn' }
      ],
      previewImages: [
        'https://cdn.prod.website-files.com/5f3bf8a18cfff95974f80423/658dab0aef485db1d69e8522_Screen%202.jpg',
        'https://cdn.prod.website-files.com/5f3bf8a18cfff95974f80423/658dab4a8cf33b0ecd008f9c_Screen%203.jpg'
      ],
      challenge: 'Netflix faced challenges with content discovery and user engagement. Users were overwhelmed by the vast content library and struggled to find content that matched their preferences.',
      solution: 'Through extensive user research and iterative design, I created a reimagined Netflix experience with enhanced personalization, improved content discovery mechanisms, and a more intuitive navigation system.'
    },
    '3': {
      id: 3,
      title: 'GreenGuru',
      client: 'GreenGuru',
      category: 'UX/UI Design',
      year: '2024',
      tagline: 'Empowering sustainable living through intuitive plant care and eco-friendly lifestyle guidance',
      heroImage: 'https://images.squarespace-cdn.com/content/v1/5e9e97a1e5e0d96b4f5e0b5e/1718655488862-IXQXQXQXQXQXQXQX/GreenGuru_Hero.png',
      keyFindings: [
        {
          title: 'Plant Care Complexity',
          description: 'New plant owners struggled with understanding specific care requirements, leading to 65% of plants dying within the first 3 months',
          icon: 'ri-plant-line'
        },
        {
          title: 'Information Overload',
          description: 'Users felt overwhelmed by conflicting advice from multiple sources, spending hours researching basic plant care',
          icon: 'ri-information-line'
        },
        {
          title: 'Lack of Personalization',
          description: 'Generic care guides failed to account for individual environments, climate zones, and user experience levels',
          icon: 'ri-user-settings-line'
        },
        {
          title: 'Low Engagement',
          description: 'Existing plant care apps had poor retention rates, with 78% of users abandoning the app after initial setup',
          icon: 'ri-line-chart-line'
        }
      ],
      quickStats: [
        { value: '85%', label: 'Plant Survival Rate' },
        { value: '4.9', label: 'App Store Rating' },
        { value: '92%', label: 'User Retention' },
        { value: '3.2M', label: 'Active Users' }
      ],
      previewImages: [
        'https://images.squarespace-cdn.com/content/v1/5e9e97a1e5e0d96b4f5e0b5e/1718655612345-IXQXQXQXQXQXQXQX/GreenGuru_Screens.png',
        'https://images.squarespace-cdn.com/content/v1/5e9e97a1e5e0d96b4f5e0b5e/1718655723456-IXQXQXQXQXQXQXQX/GreenGuru_Features.png'
      ],
      challenge: 'GreenGuru needed to transform plant care from a daunting task into an accessible, enjoyable experience. The challenge was creating an app that could provide personalized guidance while building confidence in novice plant owners and offering value to experienced gardeners.',
      solution: 'I designed an intelligent plant care companion that combines AI-powered plant identification, personalized care schedules based on user environment, and a supportive community. The solution includes smart reminders, visual health tracking, and gamification elements to maintain engagement and celebrate user success.'
    }
  };

  const project = projects[id || '1'];

  if (!project) {
    navigate('/');
    return null;
  }

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="bg-white">
      <Navigation show={showNav} />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-6 md:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#1a2942] to-[#0A1628]"></div>
        
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#FF6B4A] rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#FF6B4A] rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto text-center pt-32 pb-16">
          <div className="text-[#FF6B4A] text-sm font-medium tracking-wide mb-6 uppercase">
            {project.category} • {project.year}
          </div>

          <h1 className="text-5xl sm:text-6xl md:text-7xl font-light text-white mb-8 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            {project.title}
          </h1>

          <p className="text-xl sm:text-2xl text-white/80 mb-12 max-w-3xl mx-auto leading-relaxed">
            {project.tagline}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
            <button
              onClick={() => navigate(`/project/${project.id}`)}
              className="bg-[#FF6B4A] text-white px-10 py-5 rounded-full hover:bg-[#FF5A3A] transition-all duration-300 font-medium text-lg shadow-xl whitespace-nowrap cursor-pointer"
            >
              View Full Case Study
            </button>
            <button
              onClick={() => scrollToSection('key-findings')}
              className="border-2 border-white/30 text-white px-10 py-5 rounded-full hover:bg-white/10 transition-all duration-300 font-medium text-lg whitespace-nowrap cursor-pointer backdrop-blur-sm"
            >
              Explore Findings
            </button>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {project.quickStats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl sm:text-5xl font-bold text-[#FF6B4A] mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-white/60 uppercase tracking-wide">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <button
            onClick={() => scrollToSection('key-findings')}
            className="w-12 h-12 flex items-center justify-center text-white/60 hover:text-white transition-colors duration-300 cursor-pointer"
          >
            <i className="ri-arrow-down-line text-2xl"></i>
          </button>
        </div>
      </section>

      {/* Hero Image Preview */}
      <section className="py-16 px-6 md:px-8 bg-[#F8F6F3]">
        <div className="max-w-6xl mx-auto">
          <div className="rounded-[50px] overflow-hidden shadow-2xl">
            <img
              src={project.heroImage}
              alt={project.title}
              className="w-full h-auto"
            />
          </div>
        </div>
      </section>

      {/* Key Findings */}
      <section id="key-findings" className="py-24 px-6 md:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-light mb-6 text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Key Research Findings
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Through extensive user research and data analysis, we uncovered critical insights that shaped our design approach
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {project.keyFindings.map((finding, index) => (
              <div
                key={index}
                className="bg-[#F8F6F3] rounded-3xl p-8 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-16 h-16 rounded-2xl bg-[#FF6B4A]/10 flex items-center justify-center mb-6">
                  <i className={`${finding.icon} text-3xl text-[#FF6B4A]`}></i>
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {finding.title}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {finding.description}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <button
              onClick={() => navigate(`/project/${project.id}`)}
              className="bg-[#FF6B4A] text-white px-10 py-5 rounded-full hover:bg-[#FF5A3A] transition-all duration-300 font-medium text-lg shadow-lg whitespace-nowrap cursor-pointer inline-flex items-center gap-3"
            >
              See How We Solved These Challenges
              <i className="ri-arrow-right-line text-xl"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Challenge & Solution Preview */}
      <section className="py-24 px-6 md:px-8 bg-[#0A1628]">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-[#FF6B4A] text-sm font-medium tracking-wide mb-4 uppercase">
                The Challenge
              </div>
              <h3 className="text-3xl sm:text-4xl font-light mb-6 text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                Understanding the Problem
              </h3>
              <p className="text-white/80 text-lg leading-relaxed mb-8">
                {project.challenge}
              </p>
              <button
                onClick={() => navigate(`/project/${project.id}`)}
                className="text-[#FF6B4A] font-medium text-lg flex items-center gap-2 hover:gap-3 transition-all duration-300 cursor-pointer"
              >
                Read the Full Challenge
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>

            <div>
              <div className="text-[#FF6B4A] text-sm font-medium tracking-wide mb-4 uppercase">
                The Solution
              </div>
              <h3 className="text-3xl sm:text-4xl font-light mb-6 text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                Our Design Approach
              </h3>
              <p className="text-white/80 text-lg leading-relaxed mb-8">
                {project.solution}
              </p>
              <button
                onClick={() => navigate(`/project/${project.id}`)}
                className="text-[#FF6B4A] font-medium text-lg flex items-center gap-2 hover:gap-3 transition-all duration-300 cursor-pointer"
              >
                Explore the Solution
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Design Preview Gallery */}
      <section className="py-24 px-6 md:px-8 bg-[#F8F6F3]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-light mb-6 text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Design Highlights
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A glimpse into the redesigned experience
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {project.previewImages.map((image, index) => (
              <div key={index} className="rounded-[40px] overflow-hidden shadow-xl">
                <img
                  src={image}
                  alt={`Design preview ${index + 1}`}
                  className="w-full h-auto"
                />
              </div>
            ))}
          </div>

          <div className="text-center">
            <button
              onClick={() => navigate(`/project/${project.id}`)}
              className="bg-[#FF6B4A] text-white px-10 py-5 rounded-full hover:bg-[#FF5A3A] transition-all duration-300 font-medium text-lg shadow-lg whitespace-nowrap cursor-pointer inline-flex items-center gap-3"
            >
              View Complete Design Process
              <i className="ri-arrow-right-line text-xl"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-24 px-6 md:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl font-light mb-8 text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            The Impact
          </h2>
          <p className="text-xl text-gray-700 leading-relaxed mb-12">
            The redesigned Netflix experience resulted in significant improvements across all key metrics, 
            demonstrating the power of user-centered design and iterative testing.
          </p>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {project.quickStats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold text-[#FF6B4A] mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-600 uppercase tracking-wide">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={() => navigate(`/project/${project.id}`)}
            className="bg-[#0A1628] text-white px-12 py-6 rounded-full hover:bg-[#1a2942] transition-all duration-300 font-medium text-lg shadow-xl whitespace-nowrap cursor-pointer inline-flex items-center gap-3"
          >
            Read the Complete Case Study
            <i className="ri-arrow-right-line text-xl"></i>
          </button>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 md:px-8 bg-gradient-to-br from-[#0A1628] via-[#1a2942] to-[#0A1628]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl font-light mb-6 text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
            Want to See More?
          </h2>
          <p className="text-xl text-white/80 mb-12 leading-relaxed">
            Dive deep into the complete case study to explore the full design process, 
            detailed research findings, and comprehensive results.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate(`/project/${project.id}`)}
              className="bg-[#FF6B4A] text-white px-12 py-6 rounded-full hover:bg-[#FF5A3A] transition-all duration-300 font-medium text-lg shadow-xl whitespace-nowrap cursor-pointer"
            >
              View Full Case Study
            </button>
            <button
              onClick={() => navigate('/')}
              className="border-2 border-white/30 text-white px-12 py-6 rounded-full hover:bg-white/10 transition-all duration-300 font-medium text-lg whitespace-nowrap cursor-pointer backdrop-blur-sm"
            >
              Back to Portfolio
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
